// Tenantra Inc. - Security page: verification status + MFA settings
import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import api from '@/services/api';
import MFASettings from '@/components/MFASettings';
import { TableSkeleton } from '@/components/dashboard/TableKit';

const ID_STATUS = {
  verified: { label: 'Verified', dot: 'bg-emerald-500', text: 'text-emerald-700', action: null },
  pending: { label: 'In review', dot: 'bg-blue-500', text: 'text-blue-700', action: 'View status' },
  manual_review: { label: 'Manual review', dot: 'bg-blue-500', text: 'text-blue-700', action: 'View status' },
  needs_documents: { label: 'Documents needed', dot: 'bg-amber-500', text: 'text-amber-700', action: 'Continue' },
  rejected: { label: 'Rejected', dot: 'bg-red-500', text: 'text-red-700', action: 'Retry' },
  incomplete: { label: 'Not started', dot: 'bg-slate-300', text: 'text-slate-500', action: 'Verify identity' },
};

const Row = ({ label, desc, status, href, testId }) => (
  <a
    href={href || undefined}
    className={`lens lens-row flex items-center gap-4 px-4 py-3 ${href ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
    data-testid={testId}
  >
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900">{label}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${status.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
      {status.label}
    </span>
    {href ? (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 whitespace-nowrap">
        {status.action}<ChevronRight className="w-3.5 h-3.5" />
      </span>
    ) : <span className="w-16" />}
  </a>
);

export default function SecurityPage() {
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get('/auth/me').then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);

  const phoneOk = !!me?.phone_verified;
  const idKey = me?.landlord?.id_verification_status || 'incomplete';
  const idStatus = ID_STATUS[idKey] || ID_STATUS.incomplete;
  const fullyVerified = phoneOk && idKey === 'verified';

  return (
    <div className="space-y-6" data-testid="security-page">
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Verification</h2>
            <p className="text-xs text-slate-500">Phone and identity verification are required before you can report or view tenants.</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${fullyVerified ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="verification-overall-status">
            <span className={`w-1.5 h-1.5 rounded-full ${fullyVerified ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {me ? (fullyVerified ? 'Account verified' : 'Action required') : '…'}
          </span>
        </div>
        {!me ? (
          <TableSkeleton rows={2} spans={[6, 3, 3]} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            <Row
              label="Phone number"
              desc={me.landlord?.phone || me.phone || 'Canadian mobile number'}
              status={phoneOk ? ID_STATUS.verified : { ...ID_STATUS.incomplete, action: 'Verify phone' }}
              href={phoneOk ? null : '/landlord/verify-phone'}
              testId="verification-phone-row"
            />
            <Row
              label="Government ID"
              desc="Identity check via secure document scan"
              status={phoneOk ? idStatus : { ...ID_STATUS.incomplete, label: 'Verify phone first' }}
              href={phoneOk && idKey !== 'verified' ? '/landlord/verify' : null}
              testId="verification-identity-row"
            />
          </div>
        )}
      </section>

      <section className="pt-2 border-t border-slate-200">
        <MFASettings />
      </section>
    </div>
  );
}
